import * as R from 'ramda'
import {getSrc} from 'gatsby-plugin-image'
import {Helmet} from 'react-helmet'
import {useLocation} from '@reach/router'
import PropTypes from 'prop-types'
import React from 'react'

import {createLocalizedAlternateLinks} from 'helpers/createLocalizedAlternateLinks'
import {slugTransformer} from 'helpers/utils'

const SEO = ({countryData, pageContext, pageMetaData}) => {
  const {pathname} = useLocation()
  const {genericData, siteMetaData} = pageMetaData
  const {genericKeywords} = genericData
  const {slug, prefix} = pageContext
  const {countryCode, navLocale, keywords, technicalName} = countryData

  const hasPrefix = R.compose(R.not, R.isEmpty, R.trim)
  const prefixedSlug = hasPrefix(prefix) ? `${prefix}${slug}` : slug
  const klsSlug = slugTransformer(slug)

  const {
    defaultTitle,
    titleTemplate,
    defaultDescription,
    siteUrl,
    defaultImage,
    twitterUsername,
  } = siteMetaData.siteMetadata

  const {metaTitle, metaDescription, image, article, author} = pageMetaData

  const defaultHref =
    countryCode === 'worldwide'
      ? `${siteUrl}/${countryCode}/${prefixedSlug}`
      : `${siteUrl}/${countryCode}/${navLocale}/${prefixedSlug}`

  const seo = {
    title: metaTitle || defaultTitle,
    description: metaDescription || defaultDescription,
    image: image ? getSrc(image) : `${siteUrl}${defaultImage}`,
    url: `${siteUrl}${pathname}`,
    keywords: R.map(e => e.content, genericKeywords).join(', '),
    countryKeywords: keywords && R.map(e => e.content, keywords).join(', '),
  }

  const pageTitle = article ? seo.title : `${seo.title} ${technicalName}`

  const envWebsite = process.env.GATSBY_WEBSITE

  const handleCanonical = site => (site === 'keyrus' ? defaultHref : seo.url)

  const handleAlternateLinks = site => {
    if (!article) {
      if (site === 'keyrus') {
        return R.map(
          e => e,
          createLocalizedAlternateLinks(siteUrl, prefixedSlug, defaultHref),
        )
      }
    }
  }

  return (
    <>
      <Helmet
        title={pageTitle}
        titleTemplate={titleTemplate}
        htmlAttributes={{
          lang: navLocale,
        }}
      >
        <title>{seo.title}</title>

        <meta name="description" content={seo.description} />
        <meta name="image" content={seo.image} />

        {seo.url && <meta property="og:url" content={seo.url} />}
        {(article ? true : null) && (
          <meta property="og:type" content="article" />
        )}

        {pageTitle && <meta property="og:title" content={pageTitle} />}
        {seo.description && (
          <meta property="og:description" content={seo.description} />
        )}
        {seo.image && <meta property="og:image" content={seo.image} />}

        <meta name="twitter:card" content="summary_large_image" />
        {twitterUsername && (
          <meta name="twitter:creator" content={twitterUsername} />
        )}

        {pageTitle && <meta name="twitter:title" content={pageTitle} />}
        {seo.description && (
          <meta name="twitter:description" content={seo.description} />
        )}
        {seo.image && <meta name="twitter:image" content={seo.image} />}

        <link rel="canonical" href={handleCanonical(envWebsite)} />

        {handleAlternateLinks(envWebsite)}

        {!article && envWebsite === 'kls' && (
          <link
            rel="alternate"
            hrefLang="en"
            href={`${siteUrl}/en/en/${klsSlug}`}
          />
        )}
        {!article && envWebsite === 'kls' && (
          <link
            rel="alternate"
            hrefLang="fr"
            href={`${siteUrl}/fr/fr/${klsSlug}`}
          />
        )}
        {!article && envWebsite === 'kls' && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${siteUrl}/en/en/${klsSlug}`}
          />
        )}

        {seo.countryKeywords ? (
          <meta name="keywords" content={seo.countryKeywords} />
        ) : (
          <meta name="keywords" content={seo.keywords} />
        )}

        {author && <meta name="author" content={author} />}
      </Helmet>
    </>
  )
}

export default SEO

SEO.propTypes = {
  article: PropTypes.bool,
  author: PropTypes.string,
  countryData: PropTypes.node,
  image: PropTypes.string,
  pageContext: PropTypes.node,
  pageMetaData: PropTypes.node,
}

SEO.defaultProps = {
  article: false,
  author: null,
  countryData: null,
  image: null,
  pageContext: null,
  pageMetaData: null,
}
